import React from "react";
import "../../pages/Provider/orderKit/orderKit.scss";
import { fetchData } from "../../services/apiService";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";

function UserKitOrders() {
  const navigate = useNavigate();
  const [allOrders, setAllOrders] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);
  const [search, setSearch] = React.useState("");

  const filteredOrders = React.useMemo(() => {
    if (!search.trim()) return allOrders;

    const searchLower = search.toLowerCase();
    return allOrders.filter(
      (order) =>
        (order.id && order.id.toString().toLowerCase().includes(searchLower)) ||
        (order.attentionTo &&
          order.attentionTo.toLowerCase().includes(searchLower)) ||
        (order.trackingEmail &&
          order.trackingEmail.toLowerCase().includes(searchLower)) ||
        (order.shippingMethodCode &&
          order.shippingMethodCode.toLowerCase().includes(searchLower))
    );
  }, [allOrders, search]);

  const totalCount = filteredOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const orders = filteredOrders.slice(startIndex, endIndex);

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const tokenData = JSON.parse(atob(token.split(".")[1]));
      return tokenData.userId || tokenData.id || null;
    } catch (e) {
      console.warn("Could not parse token:", e);
      return null;
    }
  };

  const loadOrders = React.useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const userId = getUserIdFromToken();
      if (!userId) {
        setError("Unable to get user information");
        setAllOrders([]);
        return;
      }

      const res = await fetchData(
        `/api/kit-orders/get-user-kit-orders/${userId}`
      );
      const list = res?.data?.items || res?.data || [];
      setAllOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      setError("Failed to load orders");
      setAllOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const clearSearch = () => {
    setSearch("");
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1);
  }, [search]);

  React.useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  return (
    <div className="layout">
      <Header />
      <div className="testsPage">
        <div className="topBar">
          <div className="topBarLeft">
            <h1 className="pageTitle">Kit Orders</h1>
          </div>
          <div className="topBarRight">
            <div className="searchControl">
              <div className="searchBar">
                <span className="searchIcon">🔎</span>
                <input
                  className="searchInput"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by user, email, order id"
                />
                {search && (
                  <button className="clearSearch" onClick={clearSearch}>
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="ordersCard">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading orders...</p>
            </div>
          ) : error ? (
            <div className="emptyState">
              <h3>{error}</h3>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="emptyState">
              <h3>No orders found</h3>
              <p>
                {search
                  ? "Try adjusting your search or check back later."
                  : "No orders available."}
              </p>
            </div>
          ) : (
            <div className="tableWrapper">
              <table className="ordersTable">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Kits</th>
                    <th>Shipping Method</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o, idx) => {
                    let kits = [];
                    try {
                      if (typeof o.kits === "string") {
                        kits = JSON.parse(o.kits);
                      } else if (Array.isArray(o.kits)) {
                        kits = o.kits;
                      }
                    } catch (e) {
                      console.warn("Failed to parse kits:", e);
                    }

                    const getStatusClass = (status) => {
                      if (!status) return "status-unknown";
                      const statusUpper = status.toUpperCase();
                      if (statusUpper === "CONFIRMED") {
                        return "status-confirmed";
                      } else if (statusUpper === "PENDING") {
                        return "status-pending";
                      } else if (statusUpper === "CANCELLED") {
                        return "status-cancelled";
                      } else if (statusUpper === "PAYMENT_AUTHORIZED") {
                        return "status-authorized";
                      }
                      return "status-unknown";
                    };

                    return (
                      <tr key={idx}>
                        <td>{o.id || "-"}</td>
                        <td>{o.attentionTo || "-"}</td>
                        <td>{o.trackingEmail || "-"}</td>
                        <td>
                          {kits.length > 0 ? (
                            <div className="kitsList">
                              {kits.map((kit, kitIdx) => (
                                <div key={kitIdx} className="kitItem">
                                  <span className="kitName">
                                    {kit.name || kit.code || "Unknown Kit"}
                                  </span>
                                  {kit.code && (
                                    <span className="kitCode">
                                      ({kit.panels[0].code})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{o.shippingMethodCode || "-"}</td>
                        <td>
                          ${typeof o.total === "number"
                            ? `$${o.total.toFixed(2)}`
                            : o.total || "-"}
                        </td>
                        <td>
                          <span
                            className={`status ${getStatusClass(o.status)}`}
                          >
                            {o.status || "-"}
                          </span>
                        </td>
                        <td>
                          {o.createdAt
                            ? new Date(o.createdAt).toLocaleString()
                            : "-"}
                        </td>
                        <td>
                          {(o.status || "")
                            .toLowerCase()
                            .includes("pending") ? (
                            <button
                              className="payNowBtn"
                              onClick={() =>
                                navigate(`/app/user-kit-checkout/${o.id}`)
                              }
                            >
                              Pay Now
                            </button>
                          ) : (
                            "Paid"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="paginationBar">
            <button
              className="pageBtn"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span className="pageInfo">
              Page {page} of {totalPages}
            </span>
            <button
              className="pageBtn"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default UserKitOrders;
